import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Loader } from 'semantic-ui-react';
import { useFetchCustomerPersonaByIdQuery, useUpdateCustomerPersonaMutation } from './customerPersonaApiSlice';
import { selectCurrentUser } from '../auth/authSlice';
import { PAGE_SIZE, capitalizeFirstLetter, toTitleCase } from '../../constants';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { greyBox } from '../../components/reportCards';
import {
  BandButton, DetailPageShell, FormField, FormGrid, FormSection, ListNote, Tag,
  chip, inputStyle, listCard, pillButton, stack, textareaStyle,
} from '../../components/listPage';

const GENDERS = [['MALE', '남성'], ['FEMALE', '여성'], ['OTHER', '기타']];
const genderText = g => GENDERS.find(([v]) => v === g)?.[1] || '기타';

// The generator's keys, in Korean; an unknown key falls back to its Title Case.
const ATTR_LABELS = {
  customer_name: '대화 속 이름', age: '나이', title: '직업', appearance: '외모', purpose_of_visit: '방문 목적',
  primary_uses: '주요 용도', current_devices: '현재 기기', budget: '예산', values: '중요하게 여기는 것',
  worries: '걱정', hidden_interests: '숨은 관심사', technical_knowledge: '기술 이해도',
  purchase_intent: '구매 의도', disclosure_style: '정보 공개 성향', initiative_level: '대화 주도성',
  current_location: '현재 위치', current_activity: '현재 행동', current_plan: '현재 요금제',
  device_contract: '단말 약정', family_carrier: '가족 통신사', home_internet: '집 인터넷',
  credit_card: '신용카드', savings_product: '적금·저축', security_concern: '보안 우려',
  damage_history: '파손 이력', price_reaction: '가격 반응', upsell_hook: '추가 제안 포인트',
  practice_focus_areas: '연습 중점 영역', information: '정보',
};
const SUBKEY_LABELS = { value: '내용', expectations: '기대사항' };
const attrLabel = k => ATTR_LABELS[k] || toTitleCase(k);
const isScalar = v => v === null || ['string', 'number', 'boolean'].includes(typeof v);
const isGroup = v => v && typeof v === 'object' && !Array.isArray(v);
// Long values take the full row, so a two-word 나이 doesn't sit beside a paragraph of 주요 용도.
const isLong = attrs => Object.values(attrs).some(v => String(v ?? '').length > 60);
const text = v => capitalizeFirstLetter(`${v ?? ''}`);

/** Which 파트너 offer this persona, read-only: it is set per 파트너 (파트너 상세 › 고객 페르소나),
 *  so each link lands on that tab. Staff only — contractors_info is null for anyone else. */
const Partners = ({ persona }) => {
  const cs = persona.contractors_info;
  if (!cs || persona.is_dynamic) return null;
  if (!cs.length) {
    return persona.deactivated_at ? null : (
      <ListNote tone="amber">아직 어떤 파트너에도 배정되지 않아 훈련생에게 보이지 않습니다. 파트너 관리 › 파트너 상세의 <b>고객 페르소나</b> 탭에서 배정하세요.</ListNote>
    );
  }
  return (
    <div style={{ ...listCard, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
      <span style={{ fontSize: '13px', fontWeight: 600, color: '#86868b', marginRight: '4px' }}>배정된 파트너</span>
      {cs.map(c => <Link key={c.id} to={`/admin/contractors/${c.id}?tab=personas`}><Tag tone="blue">{c.name}</Tag></Link>)}
    </div>
  );
};

/** The in-store scene the trainee sees on the card. Sized by height: these are 3:4 portraits,
 *  and cropping one defeats the point (where the customer stands, what is on the table). */
const Scene = ({ persona, isMobile }) => (
  <figure style={{ margin: 0, textAlign: 'center' }}>
    <img
      src={persona.situation_url}
      alt={`${persona.int_id ? `${persona.int_id}번 고객` : '고객'} 매장 장면`}
      style={{ height: isMobile ? 'min(360px, 50vh)' : '280px', width: 'auto', maxWidth: '100%', aspectRatio: '3 / 4', objectFit: 'contain', borderRadius: '12px', display: 'block', margin: '0 auto', backgroundColor: '#F2F2F7' }}
    />
    <figcaption style={{ fontSize: '11px', color: '#86868b', marginTop: '6px' }}>매장 장면 · AI 생성 이미지</figcaption>
  </figure>
);

/** 맞춤 페르소나 only: what the trainee's own sessions said to work on. */
const Improvement = ({ aoi }) => {
  const comm = (aoi?.communication || []).filter(c => c.text);
  const products = (aoi?.product_knowledge || []).filter(p => p.name);
  if (!comm.length && !products.length) return null;
  return (
    <FormSection title="맞춤 훈련 영역">
      <div style={stack}>
        {comm.length > 0 && <FormField label="응대 스킬"><div style={greyBox}>{comm.map((c, i) => <div key={i}>· {c.text}</div>)}</div></FormField>}
        {products.length > 0 && <FormField label="제품"><div style={greyBox}>{products.map(p => p.name).join(', ')}</div></FormField>}
      </div>
    </FormSection>
  );
};

/** One persona, on its own page. Read and edit share the same sections, so 수정 swaps values
 *  for inputs in place rather than opening a different-looking form. */
const CustomerPersonaPage = () => {
  const { id } = useParams();
  const { isMobile } = useBreakpoint();
  const isSuperuser = useSelector(selectCurrentUser)?.is_superuser === true;
  const { data: persona, isLoading, isError } = useFetchCustomerPersonaByIdQuery(id);
  const [update, { isLoading: saving }] = useUpdateCustomerPersonaMutation();
  const [form, setForm] = useState(null); // non-null = editing
  const [note, setNote] = useState(null);
  useEffect(() => { setForm(null); setNote(null); }, [id]);

  const title = persona ? (persona.int_id ? `${persona.int_id}번 고객` : persona.name) : '고객 페르소나';
  const startEdit = () => {
    setNote(null);
    setForm({
      name: persona.name || '',
      gender: persona.gender || 'OTHER',
      purpose_of_visit: persona.purpose_of_visit || '',
      // Deep clone; only scalar leaves become fields, anything else is kept as-is.
      persona: JSON.parse(JSON.stringify(persona.persona || {})),
    });
  };
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setLeaf = (attr, sub, v) => setForm(f => ({ ...f, persona: { ...f.persona, [attr]: { ...f.persona[attr], [sub]: v } } }));

  const save = async () => {
    // Leaves the generator emitted as numbers (나이) stay numbers; a text input would stringify them.
    const orig = persona.persona || {};
    const out = {};
    for (const [attr, attrs] of Object.entries(form.persona)) {
      out[attr] = isGroup(attrs)
        ? Object.fromEntries(Object.entries(attrs).map(([k, v]) => [k, typeof orig[attr]?.[k] === 'number' && typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v)) ? Number(v) : v]))
        : attrs;
    }
    try {
      await update({ id: persona.id, name: form.name, gender: form.gender, purpose_of_visit: form.purpose_of_visit, persona: out }).unwrap();
      setForm(null);
      setNote({ tone: 'green', text: '페르소나를 저장했습니다.' });
    } catch {
      setNote({ tone: 'red', text: '저장에 실패했습니다.' });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' }); // the note is at the top; 저장 is at the bottom
  };

  const editing = !!form;
  const value = v => <div style={{ ...greyBox, whiteSpace: 'pre-wrap' }}>{text(v)}</div>;
  // A value that wouldn't fit on one line gets a textarea, sized to it, so nothing is cut off mid-edit.
  const field = (v, onChange, long) => (long || String(v ?? '').length > 24
    ? <textarea style={{ ...textareaStyle, minHeight: String(v ?? '').length > 80 ? '84px' : '64px' }} value={v ?? ''} onChange={e => onChange(e.target.value)} />
    : <input style={inputStyle} value={v ?? ''} onChange={e => onChange(e.target.value)} />);

  const body = () => {
    const p = editing ? form : persona;
    const attrs = Object.entries(p.persona || {}).filter(([, a]) => isGroup(a));
    return (
      <div style={stack}>
        <Partners persona={persona} />
        {note && <ListNote tone={note.tone}>{note.text}</ListNote>}
        {!persona.is_postprocessed && <ListNote tone="amber">페르소나 상세 정보를 생성 중입니다. 잠시 후 다시 확인해 주세요.</ListNote>}

        <FormSection title="개요">
          <div style={{ display: 'grid', gridTemplateColumns: persona.situation_url && !isMobile ? 'auto minmax(0, 1fr)' : 'minmax(0, 1fr)', gap: '16px', alignItems: 'start' }}>
            {persona.situation_url && <Scene persona={persona} isMobile={isMobile} />}
            <div style={{ display: 'grid', gap: '14px' }}>
              <FormField label="이름">{editing ? field(form.name, v => set('name', v)) : value(persona.name)}</FormField>
              <FormField label="성별">
                {editing ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '42px' }}>
                    {GENDERS.map(([v, label]) => <button key={v} type="button" onClick={() => set('gender', v)} style={chip(form.gender === v)}>{label}</button>)}
                  </div>
                ) : value(genderText(persona.gender))}
              </FormField>
              {persona.user_info && <FormField label="맞춤 대상">{value(persona.user_info.full_name || persona.user_info.username)}</FormField>}
              <FormField label="설명">{editing ? field(form.purpose_of_visit, v => set('purpose_of_visit', v), true) : value(persona.purpose_of_visit)}</FormField>
            </div>
          </div>
        </FormSection>

        {!editing && <Improvement aoi={persona.areas_of_improvement} />}

        {attrs.length > 0 && (
          <FormSection title="페르소나 상세">
            <FormGrid isMobile={isMobile}>
              {attrs.map(([attr, a]) => {
                const subs = Object.entries(a).filter(([, v]) => isScalar(v));
                const long = isLong(a);
                return (
                  <FormField key={attr} label={attrLabel(attr)} full={long}>
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {subs.map(([sub, v]) => (
                        <div key={sub}>
                          {subs.length > 1 && <div style={{ fontSize: '12px', fontWeight: 600, color: '#86868b', marginBottom: '4px' }}>{SUBKEY_LABELS[sub] || toTitleCase(sub)}</div>}
                          {editing ? field(v, nv => setLeaf(attr, sub, nv), sub === 'expectations') : value(v)}
                        </div>
                      ))}
                    </div>
                  </FormField>
                );
              })}
            </FormGrid>
          </FormSection>
        )}

        {editing && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button type="button" onClick={() => setForm(null)} disabled={saving} style={pillButton(false)}>취소</button>
            <button type="button" onClick={save} disabled={saving} style={{ ...pillButton(true), opacity: saving ? 0.5 : 1 }}>{saving ? '저장 중…' : '변경사항 저장'}</button>
          </div>
        )}
      </div>
    );
  };

  return (
    <DetailPageShell
      isMobile={isMobile}
      title={title}
      subtitle={[persona?.name, persona?.is_dynamic ? '맞춤 페르소나' : null, persona?.deactivated_at ? '비활성' : null].filter(Boolean).join(' · ')}
      backTo={`/customerpersonaslist?page=1&page_size=${PAGE_SIZE}`}
      actions={isSuperuser && persona && !editing && <BandButton compact={isMobile} icon="edit outline" onClick={startEdit}>수정</BandButton>}
    >
      {isLoading ? (
        <Loader active inline="centered" style={{ marginTop: '40px' }} />
      ) : isError || !persona ? (
        <div style={{ ...listCard, textAlign: 'center', color: '#86868b' }}>페르소나를 찾을 수 없습니다.</div>
      ) : body()}
    </DetailPageShell>
  );
};

export default CustomerPersonaPage;
